import api from './api';
import { ApiResponse, unwrapApiResponse } from '@/utils/api-response.util';
import { RecommendationsProgress, RecommendationsRange } from '@/types/recommendations-progress';
import {
  Calendar,
  CalendarEventDto,
  CalendarEventType,
  CalendarOptions,
  CreateCalendarDto,
  CreateCalendarEventDto,
  UpdateCalendarDto,
  UpdateCalendarEventDto,
  ScheduleSlotType,
  ScheduleSlot,
  CreateScheduleSlotDto,
  UpdateScheduleSlotDto,
  CopyScheduleSlotsDto,
  GrantAccessDto,
} from '../types/calendar';

export interface GetCalendarEventsParams {
  clientId?: number | string;
  calendarId?: number | string; // number або comma-separated string для кількох
  eventTypeIds?: string;
  dateFrom?: string; // YYYY-MM-DD
  dateTo?: string; // YYYY-MM-DD
  status?: 'pending' | 'completed' | 'overdue';
  limit?: number;
  offset?: number;
}

// DTO для створення події самопочуття
export interface CreateFeelingEventDto {
  title: string;
  scheduledLdt: string; // local datetime string (no Z suffix)
  isCompleted?: boolean;
  calendarId?: number;
}

// DTO для оновлення події самопочу
export interface UpdateFeelingEventDto {
  title?: string;
  scheduledLdt?: string; // local datetime string (no Z suffix)
  isCompleted?: boolean;
}

// DTO для створення події рідини
export interface CreateFluidEventDto {
  qty: number;
  scheduledLdt: string; // local datetime string (no Z suffix)
  description?: string;
  calendarId?: number;
  isCompleted?: boolean;
}

// DTO для оновлення події рідини
export interface UpdateFluidEventDto {
  qty?: number;
  scheduledLdt?: string; // local datetime string (no Z suffix)
  description?: string;
  isCompleted?: boolean;
}

// DTO для генерації тестових подій
export interface SeedEventsDto {
  userId: number;
  date: string; // YYYY-MM-DD
  count?: number;
}

// DTO для очищення подій
export interface ClearEventsDto {
  userId: number;
  date: string; // YYYY-MM-DD
}

// --- Dev Batch Sync ---

export interface DevClientOption {
  id: number;
  name: string;
  email: string;
}

export interface DevEventTypeOption {
  id: number;
  code: string;
  name: string;
}

export interface DevOptionsResponse {
  clients: DevClientOption[];
  eventTypes: DevEventTypeOption[];
  /** Коди типів, для яких вже є processor у CalendarBatchSyncService (готові до тестування) */
  supportedBatchSyncTypes: string[];
}

export interface BatchSyncFeelingItemDto {
  clientEventId: string;
  title: string;
  scheduledLdt: string;
  calendarId?: number;
}

export interface BatchSyncFluidItemDto {
  clientEventId: string;
  qty: number;
  scheduledLdt: string;
  title?: string;
  calendarId?: number;
  isCompleted?: boolean;
}

export interface BatchSyncTaskItemDto {
  clientEventId: string;
  title: string;
  scheduledLdt: string;
  calendarId?: number;
  isCompleted?: boolean;
}

export interface BatchSyncMedicationItemDto {
  clientEventId: string;
  scheduledLdt: string;
  medicationId?: number;
  customName?: string;
  qty?: number;
  dosage?: string;
  calendarId?: number;
  isCompleted?: boolean;
}

// AI-залежний тип — обробляється окремою (повільнішою) фазою на бекенді, строго
// послідовно. Перша AI-помилка перериває весь залишок пакету (будь-якого типу) — див.
// BatchSyncResponseData.aborted / BatchSyncResultItem.retryable.
export interface BatchSyncFoodItemDto {
  clientEventId: string;
  text: string;
  scheduledLdt: string;
  rawInput?: string;
  calendarId?: number;
  isCompleted?: boolean;
  /** ТІЛЬКИ ДЛЯ ТЕСТУВАННЯ — симулює невдачу AI-запиту без реального AI-виклику. */
  simulateFailure?: boolean;
}

// AI-залежний тип, той самий принцип, що BatchSyncFoodItemDto.
export interface BatchSyncWorkoutItemDto {
  clientEventId: string;
  text: string;
  scheduledLdt: string;
  rawInput?: string;
  calendarId?: number;
  isCompleted?: boolean;
  /** ТІЛЬКИ ДЛЯ ТЕСТУВАННЯ — симулює невдачу AI-запиту без реального AI-виклику. */
  simulateFailure?: boolean;
}

export interface BatchSyncResultItem {
  clientEventId: string;
  type: string;
  success: boolean;
  eventId?: number;
  error?: string;
  /** true — варто повторити цей item наступним sync-запитом (AI-збій/таймаут); false — проблема в даних. */
  retryable?: boolean;
}

export interface BatchSyncResponseData {
  total: number;
  succeeded: number;
  failed: number;
  /** true — пакет перервано до завершення (AI-збій або перевищено час). Items з retryable=true — відправити повторно. */
  aborted: boolean;
  results: BatchSyncResultItem[];
}

export const getCalendarEventTypes = async (): Promise<CalendarEventType[]> => {
  const response = await api.get<ApiResponse<CalendarEventType[]>>('/calendars/event-types');
  const data = unwrapApiResponse<CalendarEventType[]>(response);
  return Array.isArray(data) ? data : [];
};

export const getClientCalendarOptions = async (clientId: string | number): Promise<CalendarOptions> => {
  const response = await api.get<ApiResponse<CalendarOptions>>(`/client/${clientId}/calendar/options`);
  return unwrapApiResponse<CalendarOptions>(response);
};

export const createClientCalendar = async (clientId: string | number, data: { name: string }): Promise<Calendar> => {
  const response = await api.post<ApiResponse<Calendar>>(`/client/${clientId}/calendar`, data);
  return unwrapApiResponse<Calendar>(response);
};

export interface CreateClientCalendarEventDto {
  calendarId?: number; // без нього - календар експерта
  eventTypeId: number;
  title: string;
  scheduledLdt: string; // local datetime string (no Z suffix)
  details?: Record<string, any>;
  completedAt?: string | null;
}

export const createClientCalendarEvent = async (
  clientId: string | number,
  data: CreateClientCalendarEventDto,
): Promise<CalendarEventDto> => {
  const response = await api.post<ApiResponse<CalendarEventDto>>(`/client/${clientId}/calendar/events`, data);
  return unwrapApiResponse<CalendarEventDto>(response);
};

export const createClientFluidEvent = async (
  clientId: string | number,
  data: CreateFluidEventDto,
): Promise<CalendarEventDto> => {
  const response = await api.post<ApiResponse<CalendarEventDto>>(`/client/${clientId}/calendar/events/fluid`, data);
  return unwrapApiResponse<CalendarEventDto>(response);
};

export const createClientFeelingEvent = async (
  clientId: string | number,
  data: CreateFeelingEventDto,
): Promise<CalendarEventDto> => {
  const response = await api.post<ApiResponse<CalendarEventDto>>(`/client/${clientId}/calendar/events/feeling`, data);
  return unwrapApiResponse<CalendarEventDto>(response);
};

export const createClientTaskEvent = async (
  clientId: string | number,
  data: any,
): Promise<CalendarEventDto> => {
  const response = await api.post<ApiResponse<CalendarEventDto>>(`/client/${clientId}/calendar/events/task`, data);
  return unwrapApiResponse<CalendarEventDto>(response);
};

export const getCalendars = async (): Promise<Calendar[]> => {
  const response = await api.get<ApiResponse<Calendar[]>>('/calendars');
  const data = unwrapApiResponse<Calendar[]>(response);
  return Array.isArray(data) ? data : [];
};

export const getCalendarEvents = async (params: GetCalendarEventsParams): Promise<CalendarEventDto[]> => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, value.toString());
    }
  });

  const response = await api.get<ApiResponse<CalendarEventDto[]>>(`/calendars/events?${queryParams.toString()}`);
  const data = unwrapApiResponse<CalendarEventDto[]>(response);
  return Array.isArray(data) ? data : [];
};

// --- CRUD для календарів ---

export const createCalendar = async (data: CreateCalendarDto): Promise<Calendar> => {
  const response = await api.post<ApiResponse<Calendar>>('/calendars', data);
  return unwrapApiResponse<Calendar>(response);
};

export const getCalendarById = async (id: number): Promise<Calendar> => {
  const response = await api.get<ApiResponse<Calendar>>(`/calendars/${id}`);
  return unwrapApiResponse<Calendar>(response);
};

export const updateCalendar = async (id: number, data: UpdateCalendarDto): Promise<Calendar> => {
  const response = await api.put<ApiResponse<Calendar>>(`/calendars/${id}`, data);
  return unwrapApiResponse<Calendar>(response);
};

export const deleteCalendar = async (id: number): Promise<void> => {
  await api.delete(`/calendars/${id}`);
};

export const grantAccess = async (id: number, data: GrantAccessDto): Promise<void> => {
  await api.post(`/calendars/${id}/access`, data);
};

// --- CRUD для подій ---

export const createCalendarEvent = async (data: CreateCalendarEventDto): Promise<CalendarEventDto> => {
  const response = await api.post<ApiResponse<CalendarEventDto>>(`/calendars/events`, data);
  return unwrapApiResponse<CalendarEventDto>(response);
};

export const createFeelingEvent = async (data: CreateFeelingEventDto): Promise<CalendarEventDto> => {
  const response = await api.post<ApiResponse<CalendarEventDto>>(`/calendars/events/feeling`, data);
  return unwrapApiResponse<CalendarEventDto>(response);
};

export const updateFeelingEvent = async (id: number, data: UpdateFeelingEventDto): Promise<CalendarEventDto> => {
  const response = await api.patch<ApiResponse<CalendarEventDto>>(`/calendars/events/${id}/feeling`, data);
  return unwrapApiResponse<CalendarEventDto>(response);
};

export const createFluidEvent = async (data: CreateFluidEventDto): Promise<CalendarEventDto> => {
  const response = await api.post<ApiResponse<CalendarEventDto>>(`/calendars/events/fluid`, data);
  return unwrapApiResponse<CalendarEventDto>(response);
};

export const updateFluidEvent = async (id: number, data: UpdateFluidEventDto): Promise<CalendarEventDto> => {
  const response = await api.patch<ApiResponse<CalendarEventDto>>(`/calendars/events/${id}/fluid`, data);
  return unwrapApiResponse<CalendarEventDto>(response);
};

export const getCalendarEventById = async (id: number): Promise<CalendarEventDto> => {
  const response = await api.get<ApiResponse<CalendarEventDto>>(`/calendars/events/${id}`);
  return unwrapApiResponse<CalendarEventDto>(response);
};

export const updateCalendarEvent = async (id: number, data: UpdateCalendarEventDto): Promise<CalendarEventDto> => {
  const response = await api.patch<ApiResponse<CalendarEventDto>>(`/calendars/events/${id}`, data);
  return unwrapApiResponse<CalendarEventDto>(response);
};

export const copyCalendarEvent = async (id: number, data: { scheduledLdt: string }): Promise<CalendarEventDto> => {
  const response = await api.post<ApiResponse<CalendarEventDto>>(`/calendars/events/${id}/copy`, data);
  return unwrapApiResponse<CalendarEventDto>(response);
};

export const deleteCalendarEvent = async (id: number): Promise<void> => {
  await api.delete(`/calendars/events/${id}`);
};

export const completeEvent = async (id: number): Promise<CalendarEventDto> => {
  const response = await api.patch<ApiResponse<CalendarEventDto>>(`/calendars/events/${id}/complete`, {});
  return unwrapApiResponse<CalendarEventDto>(response);
};

// --- Dev Tools ---
export const seedEvents = async (data: SeedEventsDto): Promise<void> => {
  await api.post('/calendars/dev/seed-events', data);
};

export const clearEvents = async (data: ClearEventsDto): Promise<void> => {
  // Using DELETE method with data in body (axios supports this via 'data' property in config)
  await api.delete('/calendars/dev/clear-events', { data });
};

export const getDevOptions = async (search?: string): Promise<DevOptionsResponse> => {
  const response = await api.get<ApiResponse<DevOptionsResponse>>('/calendars/dev/options', {
    params: search ? { search } : undefined,
  });
  const data = unwrapApiResponse<DevOptionsResponse>(response);
  return {
    clients: data?.clients ?? [],
    eventTypes: data?.eventTypes ?? [],
    supportedBatchSyncTypes: data?.supportedBatchSyncTypes ?? [],
  };
};

// Контейнер пакету — той самий контракт, що backend BatchSyncEventsDto.
export interface BatchSyncPayload {
  feeling?: BatchSyncFeelingItemDto[];
  fluid?: BatchSyncFluidItemDto[];
  task?: BatchSyncTaskItemDto[];
  medication?: BatchSyncMedicationItemDto[];
  food?: BatchSyncFoodItemDto[];
  workout?: BatchSyncWorkoutItemDto[];
}

// DEV-only batch-sync — дев-сторінка завжди вимагає вибраного клієнта (userId явно в тілі),
// тому окремої "власної" (без клієнта) версії немає — єдиний клієнт цього API сьогодні.
export const devBatchSync = async (userId: number, payload: BatchSyncPayload): Promise<BatchSyncResponseData> => {
  const response = await api.post<ApiResponse<BatchSyncResponseData>>('/calendars/dev/batch-sync', {
    userId,
    ...payload,
  });
  return unwrapApiResponse<BatchSyncResponseData>(response);
};

// --- Schedule Slots ---

const scheduleSlotsApi = '/schedule-slots';

export const getScheduleSlotTypes = async (): Promise<ScheduleSlotType[]> => {
  const response = await api.get<ApiResponse<ScheduleSlotType[]>>(`${scheduleSlotsApi}/types`);
  const data = unwrapApiResponse<ScheduleSlotType[]>(response);
  return Array.isArray(data) ? data : [];
};

export const getScheduleSlots = async (): Promise<ScheduleSlot[]> => {
  const response = await api.get<ApiResponse<ScheduleSlot[]>>(scheduleSlotsApi, { params: {} });
  const data = unwrapApiResponse<ScheduleSlot[]>(response);
  return Array.isArray(data) ? data : [];
};

export const getScheduleSlotsByDay = async (dayOfWeek: number): Promise<ScheduleSlot[]> => {
  const response = await api.get<ApiResponse<ScheduleSlot[]>>(`${scheduleSlotsApi}/day/${dayOfWeek}`);
  const data = unwrapApiResponse<ScheduleSlot[]>(response);
  return Array.isArray(data) ? data : [];
};

export const getScheduleSlotById = async (id: number): Promise<ScheduleSlot> => {
  const response = await api.get<ApiResponse<ScheduleSlot>>(`${scheduleSlotsApi}/${id}`);
  return unwrapApiResponse<ScheduleSlot>(response);
};

export const createScheduleSlot = async (data: CreateScheduleSlotDto): Promise<ScheduleSlot> => {
  const response = await api.post<ApiResponse<ScheduleSlot>>(scheduleSlotsApi, data);
  return unwrapApiResponse<ScheduleSlot>(response);
};

export const updateScheduleSlot = async (id: number, data: UpdateScheduleSlotDto): Promise<ScheduleSlot> => {
  const response = await api.put<ApiResponse<ScheduleSlot>>(`${scheduleSlotsApi}/${id}`, data);
  return unwrapApiResponse<ScheduleSlot>(response);
};

export const deleteScheduleSlot = async (id: number): Promise<void> => {
  await api.delete(`${scheduleSlotsApi}/${id}`);
};

export const copyScheduleSlots = async (data: CopyScheduleSlotsDto): Promise<any> => {
  const response = await api.post(`${scheduleSlotsApi}/copy`, data);
  return unwrapApiResponse(response);
};

/** Completion of the current expert's recommendations for the client over the last `days` days (dashboard widget). */
export const getRecommendationsProgress = async (
  clientId: number | string,
  days: RecommendationsRange,
): Promise<RecommendationsProgress> =>
  unwrapApiResponse<RecommendationsProgress>(await api.get(`/client/${clientId}/recommendations-progress`, { params: { days } }));
